import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  isNoop,
  parseLightyearCsv,
  type ImportPlan,
  type LightyearParseResult,
} from '@budget/core';
import { useAuth } from '../features/auth/auth-context.js';
import {
  buildPlan,
  useImportAccounts,
  useLedgerLookup,
  useRunImport,
} from '../features/import/use-lightyear-import.js';

/**
 * Lightyear CSV import.
 *
 * Lightyear has no API, so this is the only way its transactions arrive. The
 * preview is not decoration: an import that silently did nothing because the
 * rows were already stored looks identical to one that silently failed, so
 * the page says which rows are new, which are already here, and which were
 * left out on purpose, before anything is written.
 */
export function ImportPage() {
  const { status } = useAuth();
  const authenticated = status === 'authenticated';

  const accounts = useImportAccounts({ enabled: authenticated });
  const [chosenAccountId, setChosenAccountId] = useState<string | null>(null);
  const accountId = chosenAccountId ?? accounts.data?.[0]?.id ?? null;

  const [file, setFile] = useState<{ name: string; text: string } | null>(null);

  const parse = useMemo(() => readFile(file), [file]);
  const parsed = parse?.ok === true ? parse.value : null;

  const lookup = useLedgerLookup({
    accountId,
    parsed,
    fileKey: file === null ? null : `${file.name}:${file.text.length}`,
  });

  const plan = useMemo(
    () => (parsed && lookup.data ? buildPlan(parsed, lookup.data) : null),
    [parsed, lookup.data],
  );

  const run = useRunImport();

  async function onPick(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const picked = event.target.files?.[0];
    if (!picked) return;
    run.reset();
    setFile({ name: picked.name, text: await picked.text() });
  }

  return (
    <div className="stack">
      <h1 className="page-title">Lightyear import</h1>

      <section className="card">
        <h2 className="card__title">Fájl</h2>
        <p className="muted">
          A Lightyear appban: számlakivonat exportálása CSV-ben, a számlanyitástól máig. A
          bekerülési árat a tranzakciókból számoljuk, ezért egy csonka export nem hiányos, hanem
          hibás értéket adna.
        </p>

        <div className="field">
          <label className="field__label" htmlFor="import-account">
            Melyik számlára
          </label>
          <select
            id="import-account"
            className="currency-switcher__select"
            value={accountId ?? ''}
            onChange={(event) => setChosenAccountId(event.target.value)}
            disabled={accounts.isPending || (accounts.data?.length ?? 0) === 0}
          >
            {(accounts.data ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="import-file">
            CSV fájl
          </label>
          <input
            id="import-file"
            type="file"
            accept=".csv,text/csv"
            onChange={(event) => void onPick(event)}
          />
        </div>

        {accounts.data?.length === 0 && (
          <p className="alert alert--warning">
            Nincs olyan számla, amire importálni lehetne. Tranzakció-alapú bróker számla kell hozzá.
          </p>
        )}
      </section>

      {parse?.ok === false && (
        <p className="alert alert--error">A fájlt nem sikerült beolvasni: {parse.message}</p>
      )}

      {parsed !== null && lookup.isError && (
        <p className="alert alert--error">Nem sikerült lekérdezni, mely tételek vannak már meg.</p>
      )}

      {parsed !== null && lookup.isPending && <p className="muted">Összevetés a meglévőkkel…</p>}

      {plan !== null && lookup.data && (
        <Preview
          plan={plan}
          onRun={() => {
            if (accountId === null || !lookup.data) return;
            run.mutate({ accountId, plan, lookup: lookup.data });
          }}
          pending={run.isPending}
          done={run.isSuccess}
        />
      )}

      {run.isError && (
        <p className="alert alert--error">
          Az import elszállt: {describeError(run.error)}. Semmi nem veszett el — futtasd újra, a
          meglévő tételek nem duplikálódnak.
        </p>
      )}

      {run.isSuccess && (
        <section className="card">
          <h2 className="card__title">Kész</h2>
          <p>
            {run.data.transactionsWritten} tranzakció és {run.data.instrumentsCreated} új
            instrumentum került be.
          </p>
          <p>
            <Link to="/portfolio">Irány a portfólió</Link>
          </p>
        </section>
      )}
    </div>
  );
}

function Preview({
  plan,
  onRun,
  pending,
  done,
}: {
  plan: ImportPlan;
  onRun: () => void;
  pending: boolean;
  done: boolean;
}) {
  const nothingToDo = isNoop(plan);

  return (
    <section className="card">
      <div className="card__header">
        <h2 className="card__title">Előnézet</h2>
        <span className="badge">parser v{plan.parserVersion}</span>
      </div>

      {!plan.dateOrderCertain && (
        <p className="alert alert--warning">
          A fájlban egyetlen dátum sem dönti el, hogy nap/hónap vagy hónap/nap sorrendű.
          Nap/hónapnak olvastam. Ha az eredmény hónapokat csúsztat, ez az oka.
        </p>
      )}

      <dl className="definitions">
        <dt>Új tétel</dt>
        <dd className="numeric">{plan.toInsert.length}</dd>
        <dt>Már megvan</dt>
        <dd className="numeric">{plan.alreadyPresent.length}</dd>
        <dt>Új instrumentum</dt>
        <dd className="numeric">{plan.newInstruments.length}</dd>
        <dt>Szándékosan kihagyva</dt>
        <dd className="numeric">{plan.skipped.length}</dd>
      </dl>

      {plan.newInstruments.length > 0 && (
        <>
          <h3 className="card__title">Létrejövő instrumentumok</h3>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Ticker</th>
                <th scope="col">ISIN</th>
                <th scope="col">Deviza</th>
                <th scope="col">Eszközosztály</th>
              </tr>
            </thead>
            <tbody>
              {plan.newInstruments.map((instrument) => (
                <tr key={instrument.isin}>
                  <td>{instrument.ticker}</td>
                  <td className="mono">{instrument.isin}</td>
                  <td>{instrument.currency}</td>
                  <td>{instrument.assetClass}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {plan.skipped.length > 0 && <SkippedSummary plan={plan} />}

      {plan.problems.length > 0 && (
        <>
          <h3 className="card__title">Kimaradó sorok</h3>
          <ul className="alert-list">
            {plan.problems.map((problem) => (
              <li key={problem.externalId} className="alert alert--warning">
                <span className="mono">{problem.externalId}</span> — {problem.message}
              </li>
            ))}
          </ul>
        </>
      )}

      {plan.errors.length > 0 && (
        <>
          <h3 className="card__title">Hibás sorok</h3>
          <ul className="alert-list">
            {plan.errors.map((error) => (
              <li key={error.line} className="alert alert--error">
                {error.line}. sor — {error.message}
              </li>
            ))}
          </ul>
        </>
      )}

      {nothingToDo ? (
        <p className="muted">
          {done ? 'Az import lefutott.' : 'Nincs mit importálni: minden tétel már szerepel.'}
        </p>
      ) : (
        <button type="button" className="button button--primary" onClick={onRun} disabled={pending}>
          {pending ? 'Importálás…' : `${plan.toInsert.length} tétel importálása`}
        </button>
      )}
    </section>
  );
}

function SkippedSummary({ plan }: { plan: ImportPlan }) {
  const byReason = new Map<string, number>();
  for (const row of plan.skipped) {
    byReason.set(row.reason, (byReason.get(row.reason) ?? 0) + 1);
  }

  return (
    <>
      <h3 className="card__title">Szándékosan kihagyva</h3>
      <p className="muted">
        Pénzmozgások, amikre a tranzakció-ledgernek nincs típusa. A devizaváltások díja ettől
        függetlenül bekerül.
      </p>
      <dl className="definitions">
        {[...byReason].map(([reason, count]) => (
          <Fragment key={reason}>
            <dt>{reason}</dt>
            <dd className="numeric">{count}</dd>
          </Fragment>
        ))}
      </dl>
    </>
  );
}

/**
 * A readable message from whatever was thrown.
 *
 * A PostgrestError is a plain object, not an Error, so an `instanceof Error`
 * check reports every database failure as "unknown" — which is how a 42P10
 * about ON CONFLICT reached the screen saying nothing at all.
 */
function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;

  if (typeof error === 'object' && error !== null) {
    const { message, code } = error as { message?: unknown; code?: unknown };
    if (typeof message === 'string') {
      return typeof code === 'string' ? message + ' (' + code + ')' : message;
    }
  }

  return 'ismeretlen hiba';
}

type ParseOutcome =
  { ok: true; value: LightyearParseResult } | { ok: false; message: string } | null;

function readFile(file: { name: string; text: string } | null): ParseOutcome {
  if (file === null) return null;
  try {
    return { ok: true, value: parseLightyearCsv(file.text) };
  } catch (error) {
    return { ok: false, message: error instanceof Error ? error.message : String(error) };
  }
}
